(async () => {
  if (!customElements.get("popup")) {
    const { Fancybox } = await import("@fancyapps/ui");

    class Popup extends HTMLElement {
      constructor() {
        super();

        this.popupWrapper = document.getElementById("popup");
        if (!this.popupWrapper) return;

        // If "true", show immediately & do NOT set cookies or session
        this.openByDefault = this.popupWrapper.dataset.popupOpen === "true";

        // Delay in ms (only used if openByDefault is false)
        this.openAfterMs =
          parseFloat(this.popupWrapper.dataset.popupTime) * 1000 || 0;

        // Days to hide once shown (only used if openByDefault is false)
        this.hidePopupDays =
          parseInt(this.popupWrapper.dataset.popupHide, 10) || 0;

        // Elements that close the popup
        this.closeButtons =
          this.popupWrapper.querySelectorAll("[data-popup-close]");

        // Keys used in cookie/session
        this.popupCookieKey = "popup_shown";
        this.popupSessionKey = "popup_shown_session";

        this.init();
      }

      init() {
        // If openByDefault === "true", always show the popup immediately,
        // ignoring cookie/session logic
        if (this.openByDefault) {
          Fancybox.show([{ src: this.popupWrapper, type: "html" }]);
          this.addCloseListeners();
          return;
        }

        // Otherwise, proceed with normal logic:
        if (!this.shouldShowPopup()) return;

        this.scheduleOpen();
        this.addCloseListeners();
      }

      /**
       * addCloseListeners
       * @description Attach close handlers to the popup’s [data-popup-close] elements
       */
      addCloseListeners() {
        this.closeButtons.forEach((btn) => {
          btn.addEventListener("click", () => this.closePopup());
        });
      }

      /**
       * scheduleOpen
       * @description Schedules the popup to open after `openAfterMs` (if not openByDefault).
       */
      scheduleOpen() {
        setTimeout(() => {
          this.openPopup();
        }, this.openAfterMs);
      }

      /**
       * openPopup
       * @description Opens the popup via Fancybox, then marks it shown (cookie + session).
       */
      openPopup() {
        // Double-check before opening
        if (!this.shouldShowPopup()) return;

        Fancybox.show([{ src: this.popupWrapper, type: "html" }]);

        // Mark popup as shown for this session
        sessionStorage.setItem(this.popupSessionKey, "true");

        // If we're hiding for multiple days, set a cookie
        if (this.hidePopupDays > 0) {
          this.setCookie(this.popupCookieKey, "true", this.hidePopupDays);
        }
      }

      /**
       * closePopup
       * @description Called by the close button.
       */
      closePopup() {
        Fancybox.close();
      }

      /**
       * shouldShowPopup
       * @description Check if the popup should be shown (when not openByDefault).
       */
      shouldShowPopup() {
        // If the user already saw it this session, skip
        if (sessionStorage.getItem(this.popupSessionKey) === "true") {
          return false;
        }

        // If we have a multi-day hide, also check the cookie
        if (this.hidePopupDays > 0) {
          const cookieValue = this.getCookie(this.popupCookieKey);
          if (cookieValue === "true") {
            return false;
          }
        }

        return true;
      }

      /**
       * setCookie
       * @description Set a cookie for `days` days.
       */
      setCookie(name, value, days) {
        const maxAge = 60 * 60 * 24 * days; // convert days to seconds
        document.cookie = `${name}=${value}; path=/; max-age=${maxAge}`;
      }

      /**
       * getCookie
       * @description Read a cookie by name; returns null if not found.
       */
      getCookie(name) {
        const nameEq = `${name}=`;
        const parts = document.cookie.split(";");

        for (let cookie of parts) {
          cookie = cookie.trim();
          if (cookie.indexOf(nameEq) === 0) {
            return cookie.substring(nameEq.length);
          }
        }
        return null;
      }
    }

    customElements.define("pop-up", Popup);
  }
})();
